import type { OverwatchPerchDef, OverwatchPlanDef } from './types';

/*
 * Overwatch plan geometry for the admin's top-down views (the plan preview and the attempts' shot log). The plan is the sim's ground
 * plane: x across, z away from the perch (sniper.html "How it's built"). Yaw 0 looks down +z and positive yaw turns toward +x (Unity's
 * convention, which the perch arcs use). Display only: OverwatchSim and HitTest (shared-cs) are the rules.
 */

export type Bounds = { minX: number; minZ: number; maxX: number; maxZ: number };
export type Sample = { t: number; x: number; z: number; state?: string };

const f = (v: number | null | undefined) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Everything on the plan plus the perches, padded; a 40 m square around the origin when the plan is empty. */
export function planBounds(
  plan: OverwatchPlanDef | null | undefined,
  perches: (OverwatchPerchDef | null)[] = [],
  pad = 6,
): Bounds {
  const xs: number[] = [];
  const zs: number[] = [];
  for (const z of Object.values(plan?.zones ?? {})) {
    if (!z) continue;
    xs.push(f(z.minX), f(z.maxX));
    zs.push(f(z.minZ), f(z.maxZ));
  }
  for (const p of Object.values(plan?.paths ?? {}))
    for (const q of p ?? []) {
      if (!q) continue;
      xs.push(f(q.x));
      zs.push(f(q.z));
    }
  for (const o of plan?.occluders ?? []) {
    if (!o) continue;
    xs.push(f(o.min?.[0]), f(o.max?.[0]));
    zs.push(f(o.min?.[2]), f(o.max?.[2]));
  }
  for (const p of perches) {
    if (!p) continue;
    xs.push(f(p.pos?.[0]));
    zs.push(f(p.pos?.[2]));
  }
  if (!xs.length) return { minX: -20, minZ: -20, maxX: 20, maxZ: 20 };
  return {
    minX: Math.min(...xs) - pad,
    minZ: Math.min(...zs) - pad,
    maxX: Math.max(...xs) + pad,
    maxZ: Math.max(...zs) + pad,
  };
}

/** A unit direction on the plan for a yaw in degrees. */
export function yawDir(yawDeg: number): { x: number; z: number } {
  const r = (yawDeg * Math.PI) / 180;
  return { x: Math.sin(r), z: Math.cos(r) };
}

/** The perch's yaw arc as a wedge: the eye, then the two arc ends at `lengthM`. */
export function arcWedge(perch: OverwatchPerchDef, lengthM: number) {
  const eye = { x: f(perch.pos?.[0]), z: f(perch.pos?.[2]) };
  const [a, b] = [f(perch.yawDeg?.[0]), f(perch.yawDeg?.[1])];
  const da = yawDir(a);
  const db = yawDir(b);
  return {
    eye,
    from: { x: eye.x + da.x * lengthM, z: eye.z + da.z * lengthM },
    to: { x: eye.x + db.x * lengthM, z: eye.z + db.z * lengthM },
    large: Math.abs(b - a) > 180,
  };
}

/** Where a shot went on the plan: the server's impact when known, else along its yaw to `maxRangeM`. */
export function shotEnd(
  perch: OverwatchPerchDef | undefined,
  shot: { yawDeg?: number; impact?: { x: number; z: number } | null },
  maxRangeM: number,
): { from: { x: number; z: number }; to: { x: number; z: number }; known: boolean } {
  const from = { x: f(perch?.pos?.[0]), z: f(perch?.pos?.[2]) };
  if (shot.impact) return { from, to: shot.impact, known: true };
  const d = yawDir(f(shot.yawDeg));
  return { from, to: { x: from.x + d.x * maxRangeM, z: from.z + d.z * maxRangeM }, known: false };
}

/** A figure's position at `t` by linear interpolation of its samples (held at the ends); null before its first sample. */
export function positionAt(samples: Sample[], t: number): Sample | null {
  if (!samples.length || t < samples[0]!.t) return null;
  let lo = 0;
  let hi = samples.length - 1;
  if (t >= samples[hi]!.t) return samples[hi]!;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (samples[mid]!.t <= t) lo = mid;
    else hi = mid;
  }
  const a = samples[lo]!;
  const b = samples[hi]!;
  const k = b.t > a.t ? (t - a.t) / (b.t - a.t) : 0;
  return { t, x: a.x + (b.x - a.x) * k, z: a.z + (b.z - a.z) * k, state: a.state };
}

/**
 * Reads an `OverwatchPlanExporter` file (S3-OWU): the plan itself (`{zones, paths, exits, occluders}`), or a wrapper with `plan` (and
 * optionally `sceneId` and `perches`). Throws a readable error for anything else, so a pasted file never half-applies.
 */
export function parsePlanExport(text: string): {
  plan: OverwatchPlanDef;
  sceneId?: string;
  perches?: OverwatchPerchDef[];
} {
  const raw = JSON.parse(text) as unknown;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw))
    throw new Error('The plan export is a JSON object.');
  const o = raw as Record<string, unknown>;
  const plan = (o.plan && typeof o.plan === 'object' ? o.plan : o) as Record<string, unknown>;
  const keys = ['zones', 'paths', 'exits', 'occluders'];
  if (!keys.some((k) => k in plan))
    throw new Error('No plan in this file: expected zones, paths, exits or occluders.');
  const obj = (v: unknown) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
  const arr = (v: unknown) => (Array.isArray(v) ? v : []);
  return {
    plan: {
      zones: obj(plan.zones) as OverwatchPlanDef['zones'],
      paths: obj(plan.paths) as OverwatchPlanDef['paths'],
      exits: arr(plan.exits) as string[],
      occluders: arr(plan.occluders) as OverwatchPlanDef['occluders'],
    },
    sceneId: typeof o.sceneId === 'string' ? o.sceneId : undefined,
    perches: Array.isArray(o.perches) ? (o.perches as OverwatchPerchDef[]) : undefined,
  };
}
